// app/components/AdvancedChart/hooks/useOrders.ts
import { useState, useEffect } from 'react'
import { OrderItem } from '../types'
import { ConnectorManager } from '../../Connectors/ConnectorManager'
import { getSecureItem } from '../../Connectors/utils/secureStorage'

/**
 * Baby-level explanation:
 * This hook handles fetching open orders from your /api/openorders endpoint
 * or directly from the broker connector if available
 * and also provides state for lines (so we can remove them when re-plotting).
 */
export function useOrders() {
  const [openOrders, setOpenOrders] = useState<OrderItem[]>([])
  const [openOrderLines, setOpenOrderLines] = useState<any[]>([])

  async function fetchOpenOrders() {
    try {
      // Check if we have a connected broker
      const connectorManager = ConnectorManager.getInstance()
      const enabledBrokerId =
        typeof window !== 'undefined'
          ? getSecureItem('broker_enabled') || 'nintrade'
          : 'nintrade'
      const connector = connectorManager.getConnector(enabledBrokerId)

      console.log('Current broker:', enabledBrokerId)
      console.log('Connector available:', !!connector)
      console.log('Connector connected:', connector?.isConnectedToBroker?.())

      // If we have a NINX connector and it's connected, use it directly
      if (
        enabledBrokerId === 'ninx' &&
        connector &&
        connector.isConnectedToBroker?.()
      ) {
        try {
          console.log('Fetching orders from NINX connector')
          // Use the getChartOpenOrders method for NINX
          const orders = await connector.getChartOpenOrders()
          console.log('Received orders from NINX:', orders?.length || 0)
          if (orders && orders.length > 0) {
            console.log('Sample order:', JSON.stringify(orders[0], null, 2))
            setOpenOrders(orders)
            return
          } else {
            console.log('No orders returned from NINX connector')
          }
        } catch (connectorErr) {
          console.error(
            'Failed to fetch orders from NINX connector:',
            connectorErr
          )
          // Fall back to API if connector fails
        }
      }

      // If we have a Binance connector and it's connected, use it directly
      if (
        enabledBrokerId === 'binance' &&
        connector &&
        connector.isConnectedToBroker?.()
      ) {
        try {
          console.log('Fetching orders from Binance connector')
          // Use the getChartOpenOrders method for Binance
          const orders = await connector.getChartOpenOrders()
          console.log('Received orders from Binance:', orders?.length || 0)
          if (orders && orders.length > 0) {
            console.log('Sample order:', JSON.stringify(orders[0], null, 2))
            setOpenOrders(orders)
            return
          } else {
            console.log('No orders returned from Binance connector')
          }
        } catch (connectorErr) {
          console.error(
            'Failed to fetch orders from Binance connector:',
            connectorErr
          )
          // Fall back to API if connector fails
        }
      }
      
      // If we have a Zerodha connector and it's connected, use it directly
      if (
        enabledBrokerId === 'zerodha' &&
        connector &&
        connector.isConnectedToBroker?.()
      ) {
        try {
          console.log('Fetching orders from Zerodha connector')
          // Use the getChartOpenOrders method for Zerodha
          const orders = await connector.getChartOpenOrders()
          console.log('Received orders from Zerodha:', orders?.length || 0)
          if (orders && orders.length > 0) {
            console.log('Sample order:', JSON.stringify(orders[0], null, 2))
            setOpenOrders(orders)
            return
          } else {
            console.log('No orders returned from Zerodha connector')
          }
        } catch (connectorErr) {
          console.error(
            'Failed to fetch orders from Zerodha connector:',
            connectorErr
          )
          // Fall back to API if connector fails
        }
      }

      // Default fallback to API endpoint
      console.log('Falling back to API endpoint for orders')
      const res = await fetch('/api/openorders')
      if (!res.ok) {
        throw new Error('Failed to fetch open orders!')
      }
      const orders = await res.json()
      console.log('Received orders from API:', orders?.length || 0)
      setOpenOrders(orders)
    } catch (err) {
      console.error('Error in fetchOpenOrders:', err)
    }
  }

  // fetch once on mount
  useEffect(() => {
    fetchOpenOrders()
  }, [])

  return {
    openOrders,
    setOpenOrders,
    openOrderLines,
    setOpenOrderLines,
    fetchOpenOrders
  }
}
